import { useState } from 'react';
import { useMount } from 'react-use';

import {
  DocumentAttachmentListModalBase,
  DocumentAttachmentListModalProps,
} from '@app/containers/Modal/DocumentAttachmentListModal';
import {
  AttachmentInformation,
  getAttachedAttachments,
  ParticipantRef,
} from '@app/features/apply-react-signing/libs/getAttachedAttachments';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { getDocumentsAttachments } from '@app/store/transport-layer/document/documents/attachments';
import { getDocumentsParticipants } from '@app/store/transport-layer/document/documents/participants';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { Participant } from '@app/store/transport-layer/types/participant';
import { WithId } from '@app/store/transport-layer/types/utility';

function toAttachmentInformation(
  attachments: WithId<TransportLayerAttachment['ToReceive']>[],
  participants: Participant[]
): AttachmentInformation[] {
  const participantRecord: Record<string, Participant> = {};
  participants.forEach((p) => {
    participantRecord[p.dataLabel] = p;
  });

  return attachments.map<AttachmentInformation>((a) => {
    const participant = participantRecord[a.participant.dataLabel];
    const participantReference = {
      type: participant.type,
      dataLabel: participant.dataLabel || '',
      name: participant.name || '',
      order: participant.type === 'SIGNER' ? participant.order : undefined,
    } as ParticipantRef;

    return {
      id: a.id,
      title: a.title,
      documentId: a.documentId,
      file: a.input
        ? {
            extension: a.input?.file.extension || '',
            name: a.input?.file.name || '',
            size: a.input?.file.size || 0,
            url: a.input?.file.url || '',
          }
        : undefined,
      participantReference,
    };
  });
}

export function SigningProgressDocumentAttachmentListModal({
  documentId,
  isDocumentCompleted,
  onClose,
}: DocumentAttachmentListModalProps) {
  const [attachment, setAttachments] = useState<AttachmentInformation[]>([]);

  useMount(async () => {
    const [participantsResponse, attachmentsResponse] = await Promise.all([
      getDocumentsParticipants({ documentId, workspaceId }),
      getDocumentsAttachments({ documentId, workspaceId }),
    ]);
    const converted = toAttachmentInformation(
      attachmentsResponse.attachments,
      participantsResponse.participants
    );
    setAttachments(converted);
  });

  const workspaceId = useWorkspaceId();
  const attachedAttachments = getAttachedAttachments(attachment);
  return (
    <DocumentAttachmentListModalBase
      attachments={attachedAttachments}
      documentId={documentId}
      isDocumentCompleted={isDocumentCompleted}
      onClose={onClose}
    />
  );
}
